import { withAlpha } from "@/lib/color";
// AUTO-ESTRATTO da TeamDetail.jsx (refactor strutturale Fase 10.01) — nessun cambio di logica/UI/testid.
import { useState } from "react";
import { trPositionShort, PLAYING_STYLE_IT } from "@/lib/pesTranslations";

// Stile di gioco da mostrare in rosa (compatto).
// Regola: mostra lo stile NON-"Basic", con priorità all'attacco; se entrambi non-Basic mostra
// entrambi (Att · Def); se attacco = Basic usa il difensivo; se entrambi Basic mostra "Basic".
// Ripiego sul vecchio campo singolo per i giocatori non ancora ri-scrapati.
export function rosterStyleLabel(p) {
    const tr = (s) => (s ? (PLAYING_STYLE_IT[s] || s) : "");
    const att = (p.playing_style_att || "").trim();
    const def = (p.playing_style_def || "").trim();
    const attOk = att && att.toLowerCase() !== "basic";
    const defOk = def && def.toLowerCase() !== "basic";
    if (attOk && defOk) return `${tr(att)} · ${tr(def)}`;
    if (attOk) return tr(att);
    if (defOk) return tr(def);
    // entrambi Basic (o assenti): usa att/def/vecchio campo, altrimenti nulla
    const fallback = att || def || (p.playing_style || "").trim();
    return fallback ? tr(fallback) : "";
}

export function StatBox({ label, value, accent, testId, big = false, hint }) {
    return (
        <div className="border border-t22252a px-3 py-2.5 sm:px-4 sm:py-3" data-testid={testId}>
            <div className="op-label text-[9px] sm:text-[10px]">{label}</div>
            <div
                className={(big ? "op-heading text-2xl sm:text-3xl lg:text-4xl" : "op-mono font-bold text-base sm:text-lg") + " mt-1 leading-none"}
                style={{ color: accent }}
            >
                {value}
            </div>
            {hint && (
                <div className="op-mono text-[8px] sm:text-[9px] text-t5c6370 mt-1 uppercase">{hint}</div>
            )}
        </div>
    );
}

// Box "Composizione rosa": Rosa / Ugly / NFC / Prestiti con etichette chiare,
// ognuno su una riga (leggibile sia desktop sia mobile), con stato di allerta.
export function RosterCountsBox({ main, ugly, uglyCap, nfc, nfcMax, nfcLabel, svi, sviMax, sviLabel, loans, loansMax }) {
    const rows = [
        { key: "rosa", label: "Rosa", value: `${main}`, sub: "in prima squadra", accent: "rgb(var(--tc-ffffff))", warn: false },
        { key: "ugly", label: "Ugly", value: `${ugly}/${uglyCap}`, sub: "settore giovanile", accent: "rgb(var(--tc-ff6b00))", warn: ugly > uglyCap },
        { key: "nfc", label: nfcLabel || "NFC", value: `${nfc}/${nfcMax}`, sub: "facce nuove", accent: "rgb(var(--tc-2bb6ff))", warn: nfc > nfcMax },
        { key: "svi", label: sviLabel || "SVI", value: `${svi ?? 0}/${sviMax ?? 10}`, sub: "svincolati presi", accent: "rgb(var(--tc-9d7cff))", warn: (svi ?? 0) > (sviMax ?? 10) },
        { key: "prestiti", label: "Prestiti", value: `${loans}/${loansMax}`, sub: "dati in stagione", accent: "rgb(var(--tc-e8ff00))", warn: loans >= loansMax },
    ];
    return (
        <div className="border border-t22252a px-3 py-2.5 sm:px-4 sm:py-3" data-testid="team-main-count">
            <div className="op-label text-[9px] sm:text-[10px] mb-2">Composizione rosa</div>
            <div className="flex flex-col gap-1.5">
                {rows.map((r) => (
                    <div
                        key={r.key}
                        className="flex items-center justify-between gap-2 border-l-2 pl-2 py-0.5"
                        style={{ borderColor: r.warn ? "rgb(var(--tc-ff3b30))" : r.accent }}
                        data-testid={`roster-count-${r.key}`}
                    >
                        <div className="min-w-0">
                            <div className="op-mono text-[10px] sm:text-[11px] uppercase tracking-wide text-tc9ccd3 leading-none">{r.label}</div>
                            <div className="op-mono text-[8px] sm:text-[9px] text-t5c6370 uppercase mt-0.5 leading-none truncate">{r.sub}</div>
                        </div>
                        <div
                            className="op-mono font-bold text-sm sm:text-base leading-none shrink-0"
                            style={{ color: r.warn ? "rgb(var(--tc-ff3b30))" : r.accent }}
                        >
                            {r.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// VG11 cliccabile: al clic mostra un popup con gli 11 giocatori (Nome · Ruolo · VG)
// presi in considerazione per il calcolo della media VG11.
export function Vg11Box({ value, accent, hint, players = [], vg11 }) {
    const [open, setOpen] = useState(false);
    // Calcolatore preventivo: override dei VG (solo simulazione, niente salvataggi).
    const [overrides, setOverrides] = useState({});
    const [editing, setEditing] = useState(null);

    const keyOf = (p, i) => p.entry_id || p.pes_id || i;
    const effVg = (p, i) => {
        const k = keyOf(p, i);
        const o = overrides[k];
        return o !== undefined && o !== "" ? Number(o) : (p.vg ?? null);
    };
    const simVals = players.map((p, i) => effVg(p, i)).filter((v) => v != null && !Number.isNaN(v));
    const simAvg = simVals.length ? simVals.reduce((a, b) => a + b, 0) / simVals.length : null;
    const hasOverrides = Object.values(overrides).some((v) => v !== undefined && v !== "");
    const delta = (simAvg != null && vg11 != null) ? simAvg - vg11 : null;

    return (
        <div className="relative border border-t22252a px-3 py-2.5 sm:px-4 sm:py-3" data-testid="team-vg11">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-left w-full group"
                title="Mostra gli 11 giocatori del calcolo VG11"
                data-testid="team-vg11-toggle"
            >
                <div className="op-label text-[9px] sm:text-[10px] flex items-center gap-1">
                    VG11
                    <span className="text-t5c6370 group-hover:text-t2bb6ff transition-colors">ⓘ</span>
                </div>
                <div
                    className="op-heading text-2xl sm:text-3xl lg:text-4xl mt-1 leading-none underline decoration-dotted decoration-t2bb6ff/40 underline-offset-4 group-hover:decoration-t2bb6ff"
                    style={{ color: accent }}
                >
                    {value}
                </div>
                {hint && (
                    <div className="op-mono text-[8px] sm:text-[9px] text-t5c6370 mt-1 uppercase">{hint}</div>
                )}
            </button>

            {open && (
                <>
                    {/* click-fuori per chiudere */}
                    <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setEditing(null); }} data-testid="team-vg11-overlay" />
                    <div
                        className="absolute z-50 left-0 top-full mt-2 w-72 max-w-[85vw] op-card border border-t22252a bg-t0e1014 shadow-2xl shadow-black/60 p-3"
                        data-testid="team-vg11-popover"
                    >
                        <div className="op-label text-[10px] text-t2bb6ff mb-1">// GLI 11 DEL CALCOLO VG11</div>
                        <div className="op-mono text-[9px] text-t5c6370 mb-2">Clicca un VG per simulare un valore diverso</div>
                        {players.length === 0 ? (
                            <div className="op-mono text-[11px] text-t8f95a3">Nessun giocatore disponibile.</div>
                        ) : (
                            <div className="space-y-0.5">
                                {players.map((p, i) => {
                                    const k = keyOf(p, i);
                                    const changed = overrides[k] !== undefined && overrides[k] !== "";
                                    return (
                                        <div
                                            key={k}
                                            className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-t1a1d24 transition-colors"
                                            data-testid={`team-vg11-player-${i}`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="op-mono text-[10px] text-t5c6370 w-4 shrink-0">{i + 1}</span>
                                                <span className="op-mono text-[12px] text-tffffff truncate">{p.name || "—"}</span>
                                                <span className="op-mono text-[10px] text-t8f95a3 shrink-0">{trPositionShort(p.position) || "—"}</span>
                                            </div>
                                            {editing === k ? (
                                                <input
                                                    type="number" min="40" max="99" autoFocus
                                                    value={overrides[k] ?? (p.vg ?? "")}
                                                    onChange={(e) => setOverrides((o) => ({ ...o, [k]: e.target.value }))}
                                                    onBlur={() => setEditing(null)}
                                                    onKeyDown={(e) => { if (e.key === "Enter") setEditing(null); }}
                                                    className="w-14 bg-t0a0b0d border border-t2bb6ff text-right text-[12px] font-bold op-mono px-1 py-0.5 outline-none"
                                                    style={{ color: "rgb(var(--tc-2bb6ff))" }}
                                                    data-testid={`team-vg11-input-${i}`}
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditing(k)}
                                                    className="op-mono text-[12px] font-bold shrink-0 underline decoration-dotted underline-offset-2 hover:opacity-80"
                                                    style={{ color: changed ? "rgb(var(--tc-e8ff00))" : "rgb(var(--tc-2bb6ff))" }}
                                                    title="Clicca per simulare"
                                                    data-testid={`team-vg11-vg-${i}`}
                                                >
                                                    {effVg(p, i) ?? "—"}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="flex items-center justify-between border-t border-t22252a mt-2 pt-2 px-2">
                            <span className="op-label text-[10px] text-t8f95a3">Media VG11</span>
                            <span className="op-mono text-sm font-bold" style={{ color: accent }} data-testid="team-vg11-popover-avg">
                                {vg11 != null ? vg11.toFixed(1) : "—"}
                            </span>
                        </div>
                        {hasOverrides && (
                            <div className="flex items-center justify-between mt-1 px-2" data-testid="team-vg11-sim-row">
                                <span className="op-label text-[10px] text-te8ff00">Media prevista</span>
                                <span className="op-mono text-sm font-bold flex items-center gap-1.5">
                                    <span style={{ color: "rgb(var(--tc-e8ff00))" }} data-testid="team-vg11-sim-avg">{simAvg != null ? simAvg.toFixed(1) : "—"}</span>
                                    {delta != null && Math.abs(delta) >= 0.05 && (
                                        <span className="op-mono text-[10px]" style={{ color: delta > 0 ? "rgb(var(--tc-7ce38b))" : "rgb(var(--tc-ff6b6b))" }} data-testid="team-vg11-sim-delta">
                                            {delta > 0 ? "+" : ""}{delta.toFixed(1)}
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                        {hasOverrides && (
                            <button
                                type="button"
                                onClick={() => { setOverrides({}); setEditing(null); }}
                                className="w-full mt-2 op-mono text-[10px] uppercase tracking-wider py-1.5 border border-t8f95a3/40 text-t8f95a3 hover:text-tffffff hover:border-tffffff transition-colors"
                                data-testid="team-vg11-sim-reset"
                            >
                                ↺ Azzera simulazione
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// Anti-buyback (UI): un giocatore ceduto a titolo definitivo da una squadra non
// può tornare a quella stessa squadra nella stagione X (dalla X+1 sì). Ritorna il motivo
// del blocco (stringa) o null. `buyerTeamId` = la mia squadra (l'acquirente).
export function buybackReason(p, buyerTeamId, currentSeason) {
    if (!buyerTeamId || currentSeason == null) return null;
    const origin = p?.last_sold_from_team_id;
    const soldSeason = p?.last_sold_season;
    if (!origin || soldSeason == null) return null;
    if (String(origin) === String(buyerTeamId) && Number(currentSeason) <= Number(soldSeason)) {
        return `Anti-buyback: ceduto a titolo definitivo nella stagione ${soldSeason}; non potrai riacquistarlo prima della stagione ${Number(soldSeason) + 1}.`;
    }
    return null;
}


export function formatMln(v) {
    if (v == null) return "—";
    if (v === 0) return "0";
    const n = v / 1_000_000;
    if (n >= 100) return `${n.toFixed(0)}mln`;
    return `${n.toFixed(2)}mln`;
}

export function positionToColor(pos) {
    if (!pos) return "rgb(var(--tc-8f95a3))";
    if (pos === "GK") return "rgb(var(--tc-dba110))";
    if (["RB", "CB", "LB"].includes(pos)) return "rgb(var(--tc-2369d9))";
    if (["DMF", "CMF", "AMF", "LMF", "RMF"].includes(pos)) return "rgb(var(--tc-4b9f23))";
    if (["LWF", "RWF", "SS", "CF"].includes(pos)) return "rgb(var(--tc-be262e))";
    return "rgb(var(--tc-8f95a3))";
}

export function ListingBadge({ type }) {
    if (!type) return null;
    const loan = type === "loan";
    const color = loan ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-7ce38b))";
    const label = loan ? "🔁 In prestito" : "🏷️ In vendita";
    return (
        <span
            className="op-mono text-[9px] uppercase px-1 py-0 rounded-sm border whitespace-nowrap leading-none"
            style={{ color, borderColor: withAlpha(color, "66") }}
            title={label}
            data-testid="roster-listing-badge"
        >
            {label}
        </span>
    );
}

export const LOAN_KIND_IT = {
    loan: "prestito secco",
    loan_option: "diritto di riscatto",
    loan_obligation: "obbligo di riscatto",
};

// Evidenzia i giocatori in prestito: "IN PRESTITO da X" (presi in prestito)
// oppure "IN PRESTITO a X" (ceduti in prestito).
export function LoanBadge({ p }) {
    if (!p) return null;
    const fmtBb = p.loan_buyback_amount
        ? ` · riscatto €${(p.loan_buyback_amount / 1e6).toFixed(0)}M`
        : "";
    if (p.is_on_loan) {
        const from = p.loan_origin_team_name || "altra squadra";
        const kindTxt = LOAN_KIND_IT[p.loan_kind] || "prestito";
        const hasBuyback = p.loan_kind === "loan_option" || p.loan_kind === "loan_obligation";
        return (
            <>
                <span
                    className="op-mono text-[9px] uppercase px-1 py-0 rounded-sm border whitespace-nowrap leading-none"
                    style={{ color: "rgb(var(--tc-ff9d2b))", borderColor: "rgb(var(--tc-ff9d2b) / 0.4)" }}
                    title={`In prestito da ${from} (${kindTxt})${fmtBb}`}
                    data-testid={`loan-in-badge-${p.entry_id}`}
                >
                    🔁 In prestito da {from}
                </span>
                {hasBuyback ? (
                    <span
                        className="op-mono text-[9px] uppercase px-1 py-0 rounded-sm border whitespace-nowrap leading-none"
                        style={{ color: "rgb(var(--tc-ff3b30))", borderColor: "rgb(var(--tc-ff3b30) / 0.4)" }}
                        title={`Riscatto da definire a fine stagione (${kindTxt})${fmtBb} — non cedibile né prestabile finché il riscatto non è risolto`}
                        data-testid={`loan-lock-badge-${p.entry_id}`}
                    >
                        🔒 Riscatto da definire · non cedibile
                    </span>
                ) : (
                    <span
                        className="op-mono text-[9px] uppercase px-1 py-0 rounded-sm border whitespace-nowrap leading-none"
                        style={{ color: "rgb(var(--tc-8f95a3))", borderColor: "rgb(var(--tc-8f95a3) / 0.4)" }}
                        title="In prestito: non cedibile, torna alla squadra d'origine a fine stagione"
                        data-testid={`loan-lock-badge-${p.entry_id}`}
                    >
                        🔒 Non cedibile
                    </span>
                )}
            </>
        );
    }
    if (p.is_loaned_out) {
        const to = p.playing_team_name || "altra squadra";
        return (
            <span
                className="op-mono text-[9px] uppercase px-1 py-0 rounded-sm border whitespace-nowrap leading-none"
                style={{ color: "rgb(var(--tc-8f95a3))", borderColor: "rgb(var(--tc-8f95a3) / 0.4)" }}
                title={`In prestito a ${to}`}
                data-testid={`loan-out-badge-${p.entry_id}`}
            >
                🔁 In prestito a {to}
            </span>
        );
    }
    return null;
}

export function vgColor(vg) {
    if (!vg) return "rgb(var(--tc-8f95a3))";
    if (vg >= 85) return "rgb(var(--tc-ff6b00))";
    if (vg >= 80) return "rgb(var(--tc-e8ff00))";
    if (vg >= 75) return "rgb(var(--tc-2bb6ff))";
    return "rgb(var(--tc-c9ccd3))";
}

/**
 * Color rule for the two wage cells: the one that matches `applied` (the cheaper, applied wage)
 * is highlighted green; the other is yellow (rejected).
 * When values are equal, both fall back to yellow to avoid visual noise.
 */
export function wageColor(value, applied) {
    if (value == null || applied == null) return "rgb(var(--tc-5c6370))";
    return value === applied ? "rgb(var(--tc-2bb6ff))" : "rgb(var(--tc-e8ff00))";
}


// -------------- Make Offer Modal --------------
export function renewEligibility(p) {
    const years = p.contract_years;
    const expiring = years == null || years <= 1;
    const newWage = p.purchase_wage ?? p.new_rule_wage ?? p.computed_wage ?? 0;
    const current = p.applied_wage ?? 0;
    if (expiring) return { allowed: true, reason: "Contratto in scadenza (≤1 anno): rinnovo libero" };
    if (newWage > current) return { allowed: true, reason: "Nuovo stipendio superiore: rinnovo permesso" };
    return { allowed: false, reason: "Rinnovo bloccato: con ≥2 anni di contratto puoi rinnovare solo se il nuovo stipendio è superiore a quello attuale" };
}

